import { z } from 'zod';
import type { SearchMode } from '../components/ContractSearchForm';

/**
 * Contract Search Form Validation Utilities
 *
 * This module provides Zod schemas for validating the contract search form with conditional validation
 * based on the selected search mode. The validation strategy uses discriminated unions to ensure
 * type safety while supporting different validation rules for different search modes.
 *
 * **Design Approach:**
 * Instead of using a complex `superRefine` with manual error handling, this module uses:
 * - Individual field schemas for clear, reusable validation rules
 * - Conditional schemas that extend a base schema for each search mode
 * - A union type that combines all possible valid states
 * - A dynamic function to get the appropriate schema based on search mode
 *
 * **Validation Rules:**
 * - VIN & Name Mode: Requires valid 17-character VIN and customer name
 * - Contract Number Mode: Requires contract number only
 * - Fields for the inactive mode are optional and cleared automatically
 */

// =============================================================================
// Individual Field Schemas
// =============================================================================

/**
 * Validation schema for VIN (Vehicle Identification Number).
 * VINs must be exactly 17 characters long as per automotive industry standards.
 */
export const vinSchema = z
  .string()
  .trim()
  .length(17, 'VIN must be 17 characters when search mode is VIN & Name.');

/**
 * Validation schema for customer names.
 * Accepts full names or last names - allows flexible user input.
 */
export const customerNameSchema = z
  .string()
  .trim()
  .min(1, 'Customer Name is required when search mode is VIN & Name.');

/**
 * Validation schema for contract numbers.
 * The exact format requirements may vary by business rules.
 */
export const contractNumberSchema = z
  .string()
  .trim()
  .min(1, 'Contract Number is required when search mode is Contract Number.');

// =============================================================================
// Base Schema and Conditional Extensions
// =============================================================================

/**
 * Base form schema that defines the structure all search modes share.
 * All fields except searchMode are optional at this level.
 */
const baseFormSchema = z.object({
  searchMode: z.enum(['vinAndName', 'contractNumber']),
  vin: z.string().optional(),
  customerName: z.string().optional(),
  contractNumber: z.string().optional(),
});

/**
 * Schema for VIN & Name search mode.
 * Requires VIN and customerName, makes contractNumber optional with empty default.
 *
 * This creates a discriminated union member where searchMode = 'vinAndName'
 * and the required fields are validated accordingly.
 */
export const vinAndNameSchema = baseFormSchema.extend({
  searchMode: z.literal('vinAndName'),
  vin: vinSchema,
  customerName: customerNameSchema,
  contractNumber: z.string().optional().default(''),
});

/**
 * Schema for Contract Number search mode.
 * Requires contractNumber, makes VIN and customerName optional with empty defaults.
 *
 * This creates a discriminated union member where searchMode = 'contractNumber'
 * and only the contract number field is required.
 */
export const contractNumberModeSchema = baseFormSchema.extend({
  searchMode: z.literal('contractNumber'),
  contractNumber: contractNumberSchema,
  vin: z.string().optional().default(''),
  customerName: z.string().optional().default(''),
});

// =============================================================================
// Runtime Validation and Type Inference
// =============================================================================

/**
 * Dynamic validation function that returns the appropriate schema based on the current search mode.
 *
 * This is useful for mode-specific validation or when you need to validate against
 * the exact requirements of the current mode rather than the union.
 *
 * @param searchMode - The current search mode
 * @returns The appropriate Zod schema for the given mode
 *
 * @example
 * ```tsx
 * const currentSchema = getValidationSchema(form.getFieldValue('searchMode'));
 * const result = currentSchema.safeParse(formValues);
 * ```
 */
export function getValidationSchema(searchMode: SearchMode) {
  return searchMode === 'vinAndName'
    ? vinAndNameSchema
    : contractNumberModeSchema;
}

/**
 * Union schema for runtime validation that accepts either search mode.
 *
 * This is the main schema used by TanStack Form for validation. It creates a discriminated
 * union that validates based on the searchMode field, automatically applying the correct
 * validation rules for each mode.
 *
 * **Why Union Instead of SuperRefine:**
 * - Better type inference and TypeScript support
 * - Clearer error messages that map directly to the appropriate fields
 * - Easier to test and maintain
 * - More performant as Zod can optimize the validation path
 *
 * @example
 * ```tsx
 * const form = useForm({
 *   validators: {
 *     onChange: formValidationSchema,
 *   },
 * });
 * ```
 */
export const formValidationSchema = z.union([
  vinAndNameSchema,
  contractNumberModeSchema,
]);

// =============================================================================
// Type Exports
// =============================================================================

/**
 * TypeScript type for form values, inferred from the base schema.
 *
 * This provides the broadest type that covers all possible form states,
 * where fields may be optional depending on the search mode.
 *
 * For more specific typing based on search mode, use:
 * - `z.infer<typeof vinAndNameSchema>` for VIN & Name mode
 * - `z.infer<typeof contractNumberModeSchema>` for Contract Number mode
 */
export type FormValues = z.infer<typeof baseFormSchema>;
